'use client'

import { useState, useRef, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./dialog"
import { Button } from "./button"
import { Progress } from "./progress"
import { Badge } from "./badge"
import { Upload, Loader2, CheckCircle2, AlertTriangle, RefreshCw, X, FolderOpen, Mic2, Play, Pause, ChevronDown } from 'lucide-react'
import { cn } from "../../lib/utils"
import { toast } from 'react-hot-toast'
import { useAuth } from '../../services/auth'
import { useCredits } from '../hooks/credits'
import { CreditWarning } from '../dashboard/creditstatus'

interface AudioProcessingError {
  message: string;
  details?: string;
}

const ALLOWED_AUDIO_TYPES = ['audio/wav', 'audio/mp3', 'audio/mpeg']
const FILE_SIZE_LIMIT = 15 * 1024 * 1024 // 15MB

export default function NoiseRemovalCard() {
  const { processCredits, refundCredits, hasEnoughCredits } = useCredits()
  const { refreshJWT, isAuthenticated } = useAuth()
  
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'uploading' | 'processing' | 'completed' | 'error'>('idle')
  const [error, setError] = useState<AudioProcessingError | null>(null)
  const [showGuide, setShowGuide] = useState(false)
  const [processedAudioUrl, setProcessedAudioUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!isModalOpen) {
      handleReset()
    }
  }, [isModalOpen])

  useEffect(() => {
    return () => {
      if (processedAudioUrl) {
        URL.revokeObjectURL(processedAudioUrl)
      }
    }
  }, [processedAudioUrl])

  const handleReset = () => {
    setFile(null)
    setError(null)
    setProcessingStatus('idle')
    setUploadProgress(0)
    if (processedAudioUrl) {
      URL.revokeObjectURL(processedAudioUrl)
      setProcessedAudioUrl(null)
    }
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const validateFile = (selectedFile: File): boolean => {
    if (!ALLOWED_AUDIO_TYPES.includes(selectedFile.type)) {
      setError({
        message: 'Invalid file type',
        details: 'Please select an audio file (MP3, WAV)'
      })
      return false
    }

    if (selectedFile.size > FILE_SIZE_LIMIT) {
      setError({
        message: 'File too large',
        details: 'Please select a file smaller than 15MB'
      })
      return false
    }

    return true
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    if (!hasEnoughCredits()) {
      toast.error('You need at least 2 credits to process an audio file')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    if (validateFile(selectedFile)) {
      setFile(selectedFile)
      setError(null)
      handleUpload(selectedFile)
    }
  }

  const handleUpload = async (selectedFile: File) => {
    if (!selectedFile || isUploading || !isAuthenticated) return

    try {
      const success = await processCredits();
      if (!success) {
        toast.error('Insufficient credits to process this file');
        return;
      }

      setIsUploading(true)
      setProcessingStatus('uploading')
      setUploadProgress(0)
      setError(null)

      const formData = new FormData()
      formData.append('audio', selectedFile)

      // Simulate upload progress
      const uploadInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(uploadInterval)
            return 90
          }
          return prev + 10
        })
      }, 300)

      const token = await refreshJWT()
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

      const response = await fetch(`${BACKEND_URL}/api/noise/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      })

      clearInterval(uploadInterval)
      setUploadProgress(100)

      if (!response.ok) {
        await refundCredits() // Refund credits on failure
        const errorData = await response.json()
        throw new Error(errorData.error || 'Processing failed')
      }

      setProcessingStatus('processing')
      
      const audioBlob = await response.blob()
      if (!audioBlob.type.startsWith('audio/')) {
        throw new Error('Invalid response from server')
      }

      if (processedAudioUrl) {
        URL.revokeObjectURL(processedAudioUrl)
      }

      const url = URL.createObjectURL(audioBlob)
      setProcessedAudioUrl(url)
      setProcessingStatus('completed')
      toast.success('Audio processed successfully!')

    } catch (err) {
      await refundCredits() // Refund credits on failure
      setProcessingStatus('error')
      setError({
        message: 'Processing failed',
        details: err instanceof Error ? err.message : 'Unknown error occurred'
      })
      toast.error('Failed to process the audio file')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files?.length > 0) {
      const droppedFile = e.dataTransfer.files[0]
      if (validateFile(droppedFile)) {
        setFile(droppedFile)
        setError(null)
        handleUpload(droppedFile)
      }
    }
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play().catch(err => {
          setError({
            message: 'Playback failed',
            details: 'Could not play the audio file'
          })
        })
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleDownload = () => {
    if (processedAudioUrl && file) {
      const a = document.createElement('a')
      a.href = processedAudioUrl
      a.download = `processed_${file.name}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const getStatusDisplay = () => {
    switch (processingStatus) {
      case 'uploading':
        return (
          <div className="flex items-center justify-center gap-2 text-primary">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Uploading... {uploadProgress}%</span>
          </div>
        )
      case 'processing':
        return (
          <div className="flex items-center justify-center gap-2 text-primary">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Processing your audio...</span>
          </div>
        )
      case 'completed':
        return (
          <div className="flex items-center justify-center gap-2 text-green-500">
            <CheckCircle2 className="w-4 h-4" />
            <span>Processing Complete!</span>
          </div>
        )
      case 'error':
        return (
          <div className="flex items-center justify-center gap-2 text-red-500">
            <AlertTriangle className="w-4 h-4" />
            <span>{error?.message || 'Processing Failed'}</span>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogTrigger asChild>
        <div className="bg-white dark:bg-[#1e293b]/60 rounded-3xl p-6 sm:p-8 lg:p-10 border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-colors min-h-[350px] lg:min-h-[400px] cursor-pointer">
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div className="w-12 h-12 lg:w-16 lg:h-16 bg-orange-100 dark:bg-orange-500/10 rounded-full flex items-center justify-center">
              <Mic2 className="w-6 h-6 lg:w-8 lg:h-8 text-orange-500" />
            </div>
            <Badge className="bg-orange-400 hover:bg-orange-500 text-white px-3 py-1 lg:px-4 lg:py-1.5 text-sm lg:text-base">
              Try it now
            </Badge>
          </div>
          <h3 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-gray-100 mb-3 lg:mb-4">Remove Noise</h3>
          <p className="text-base sm:text-lg lg:text-[19px] leading-relaxed text-gray-600 dark:text-gray-400">
            Enhance your audio quality by removing background noise and unwanted sounds. Our AI technology ensures crystal-clear audio while preserving the original voice quality.
          </p>
        </div>
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl">
        <div className="relative w-full max-w-2xl mx-auto">
          <CreditWarning/>
          {/* Curved corners decorative elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-16 h-16 bg-orange-400/20 rounded-br-[100px]" />
            <div className="absolute top-0 right-0 w-16 h-16 bg-orange-400/20 rounded-bl-[100px]" />
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-orange-400/20 rounded-tr-[100px]" />
            <div className="absolute bottom-0 right-0 w-16 h-16 bg-orange-400/20 rounded-tl-[100px]" />
          </div>

          {/* Main content */}
          <div className="relative bg-white rounded-3xl shadow-sm p-8 min-h-[400px]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold">Upload your files</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsModalOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div
              className={cn(
                "relative border-2 border-dashed rounded-3xl p-12 text-center transition-all duration-300",
                isDragging ? 'border-primary bg-primary/5 scale-[0.99]' : 'border-gray-200',
                processingStatus !== 'idle' ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50 hover:bg-gray-50/50 cursor-pointer'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => processingStatus === 'idle' && handleBrowseClick()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept={ALLOWED_AUDIO_TYPES.join(',')}
                disabled={processingStatus !== 'idle'}
              />
              
              <div className="flex flex-col items-center justify-center gap-6">
                {/* Icon container with gradient background */}
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
                  <Upload className="w-12 h-12 text-orange-500" />
                </div>

                <div className="space-y-2">
                  <p className="text-xl font-medium text-gray-700">
                    Drop your files here
                  </p>
                  <div className="flex items-center justify-center gap-2 text-gray-500">
                    <span>or</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleBrowseClick()
                      }}
                      className="text-primary hover:text-primary/80 font-medium flex items-center gap-1"
                      disabled={processingStatus !== 'idle'}
                    >
                      <FolderOpen className="w-4 h-4" />
                      Browse
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">
                    Supported formats: MP3, WAV (max 15MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Status and Progress Section */}
            <div className="mt-6 space-y-4">
              {getStatusDisplay()}

              {uploadProgress > 0 && processingStatus !== 'completed' && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{processingStatus === 'processing' ? 'Processing...' : 'Uploading...'}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600">{error.message}</p>
                  {error.details && (
                    <p className="text-sm text-red-500">{error.details}</p>
                  )}
                </div>
              )}

              {processingStatus === 'error' && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => file && handleUpload(file)}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry Upload
                  </Button>
                </div>)}

{processedAudioUrl && (
  <div className="space-y-4">
    <div className="flex items-center justify-between gap-4">
      <Button
        onClick={togglePlay}
        variant="outline"
        size="icon"
        className="w-12 h-12"
      >
        {isPlaying ? 
          <Pause className="w-6 h-6" /> : 
          <Play className="w-6 h-6" />
        }
      </Button>
      <div className="flex-1 text-sm text-center text-gray-600">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
      <audio 
        ref={audioRef}
        src={processedAudioUrl}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        className="hidden"
      />
      <Button
        onClick={handleDownload}
        variant="default"
        size="lg"
        className="gap-2"
      >
        <CheckCircle2 className="w-4 h-4" />
        Download Processed Audio
      </Button>
    </div>
  </div>
)}
</div>

<div className="mt-8">
<button
  onClick={() => setShowGuide(!showGuide)}
  className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 rounded-lg text-left"
>
  <span className="font-medium">Step-by-step guide</span>
  <ChevronDown className={`w-5 h-5 transition-transform ${showGuide ? 'rotate-180' : ''}`} />
</button>
{showGuide && (
  <div className="mt-4 p-4 space-y-3 text-sm text-gray-600">
    <p>1. Select or drag an audio file (MP3 or WAV format)</p>
    <p>2. Wait for the upload to complete</p>
    <p>3. Our AI will process and remove background noise</p>
    <p>4. Preview the processed audio</p>
    <p>5. Download your enhanced audio file</p>
  </div>
)}
</div>
</div>
</div>
</DialogContent>
</Dialog>
)
}